/** One life-cycle module as the wizard names it: a display label, and the key a calculation type uses. */
export type LifecycleStage = {
  /** Human-readable, e.g. "A4 - Transport". */
  label: string;
  /** The key a calculation type's stages carry, e.g. "A4". Not unique within a group — see A1-A3. */
  value: string;
};

export type LifecycleGroup = {
  headline: string;
  /** The range the group covers, e.g. "B1 - B7". */
  span: string;
  stages: LifecycleStage[];
};

/**
 * The life cycle, as the wizard presents it: five groups over the EN 15978 modules.
 *
 * Shared so the Calculation prerequisites step and the start-project dialog name and group the stages
 * identically. It was written out inside IncludedPhases.vue, which made it the only description of the
 * life cycle in the app — fine until a second surface needed to show the same thing.
 *
 * Production is three entries on ONE key. Calculation types carry the combined module `A1A3` (stage id
 * 0 in Hasura, which coexists with separate A1/A2/A3 ids nothing uses), so all three columns light
 * together and the step toggles the group as a unit. Any renderer has to cope with a group whose
 * stages share a value.
 */
export const LIFECYCLE_GROUPS: LifecycleGroup[] = [
  {
    headline: "Production",
    span: "A1 - A3",
    stages: [
      { label: "A1 - Raw material supply", value: "A1A3" },
      { label: "A2 - Transport", value: "A1A3" },
      { label: "A3 - Manufacturing", value: "A1A3" },
    ],
  },
  {
    headline: "Construction",
    span: "A4 - A5",
    stages: [
      { label: "A4 - Transport", value: "A4" },
      { label: "A5 - Installation", value: "A5" },
    ],
  },
  {
    headline: "Use",
    span: "B1 - B7",
    stages: [
      { label: "B1 - Use", value: "B1" },
      { label: "B2 - Maintenance", value: "B2" },
      { label: "B3 - Repair", value: "B3" },
      { label: "B4 - Replacement", value: "B4" },
      { label: "B5 - Refurbishment", value: "B5" },
      { label: "B6 - Operational Energy Use", value: "B6" },
      { label: "B7 - Operational Water User", value: "B7" },
    ],
  },
  {
    headline: "End of Life",
    span: "C1 - C4",
    stages: [
      { label: "C1 - Demolition", value: "C1" },
      { label: "C2 - Transport", value: "C2" },
      { label: "C3 - Waste processing", value: "C3" },
      { label: "C4 - Disposal", value: "C4" },
    ],
  },
  {
    headline: "Beyond System Boundary",
    span: "D",
    stages: [{ label: "D - Reuse, recovery, recycling", value: "D" }],
  },
];

/** The code to print for a stage key. Only the combined production module differs from its key. */
export const stageDisplayLabel = (value: string): string => (value === "A1A3" ? "A1–A3" : value);

/**
 * What a stage key covers, for a chip's accessible name. Joins every catalogue entry sharing the key,
 * so `A1A3` reads as all three production modules rather than pretending to be one of them.
 */
export const stageFullName = (value: string): string =>
  LIFECYCLE_GROUPS.flatMap((group) => group.stages)
    .filter((stage) => stage.value === value)
    .map((stage) => stage.label)
    .join(", ") || value;

/** One selectable module per group, in order. Production collapses to a single `A1A3` entry. */
export type LifecycleChipGroup = { headline: string; span: string; values: string[] };

export const LIFECYCLE_CHIP_GROUPS: LifecycleChipGroup[] = LIFECYCLE_GROUPS.map((group) => ({
  headline: group.headline,
  span: group.span,
  values: [...new Set(group.stages.map((stage) => stage.value))],
}));

/** Every stage key the catalogue knows, for asserting nothing a calculation type sends goes unplaced. */
export const KNOWN_STAGE_VALUES: string[] = LIFECYCLE_CHIP_GROUPS.flatMap((group) => group.values);
